import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useAuth } from "@/auth";
import { Card, Button, SeverityPill, fmtDateTime } from "@/components/primitives";

const ROLE_LABEL = { super_admin: "Super admin", admin: "Admin", sales: "Sales" };

function Modal({ title, children, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(4,6,15,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }} onClick={onClose}>
      <Card style={{ width: 460, padding: 26 }} onClick={(e) => e.stopPropagation()}>
        <div className="num" style={{ fontSize: 19, fontWeight: 500, color: "var(--ui-text-hi)", marginBottom: 16 }}>{title}</div>
        {children}
      </Card>
    </div>
  );
}

export default function Users() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ["users"], queryFn: endpoints.listUsers });
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState(""); const [email, setEmail] = useState(""); const [role, setRole] = useState("sales");
  const [setupLink, setSetupLink] = useState(""); const [err, setErr] = useState("");

  const users = data?.users || [];
  const inputStyle = { width: "100%", background: "var(--ui-bg)", border: "1px solid var(--ui-line)", borderRadius: 10, padding: "11px 13px", color: "var(--ui-text-hi)", fontFamily: "'Inter', sans-serif", fontSize: 14, marginTop: 6 };
  const colW = { name: 240, role: 160, status: 120, last: 180, action: 240 };

  const create = async () => {
    setErr("");
    try {
      const r = await endpoints.createUser({ name, email, role });
      setSetupLink(window.location.origin + r.setupPath);
      setName(""); setEmail(""); setRole("sales");
      await qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) { setErr(e?.response?.data?.detail || "Could not create user"); }
  };
  const toggleStatus = async (u) => {
    await endpoints.editUser(u.email, { status: u.status === "disabled" ? "active" : "disabled" });
    await qc.invalidateQueries({ queryKey: ["users"] });
  };
  const changeRole = async (u, r) => {
    await endpoints.editUser(u.email, { role: r });
    await qc.invalidateQueries({ queryKey: ["users"] });
  };
  const reset = async (u) => {
    const r = await endpoints.resetUser(u.email);
    setSetupLink(window.location.origin + r.setupPath);
    await qc.invalidateQueries({ queryKey: ["users"] });
  };
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try { await endpoints.deleteUser(u.email); await qc.invalidateQueries({ queryKey: ["users"] }); }
    catch (e) { window.alert(e?.response?.data?.detail || "Could not delete"); }
  };

  const sel = { background: "var(--ui-bg)", border: "1px solid var(--ui-line)", borderRadius: 8, padding: "6px 10px", color: "var(--ui-text-hi)", fontFamily: "'Inter',sans-serif", fontSize: 12.5 };

  return (
    <div>
      <div className="flex" style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <div className="num" style={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.9px", color: "var(--ui-text-hi)" }}>Users</div>
          <div className="prose-i" style={{ fontSize: 14, color: "var(--ui-text-lo)", marginTop: 6 }}>Manage who can sign in · super admin only</div>
        </div>
        <Button data-testid="create-user-btn" onClick={() => { setCreateOpen(true); setSetupLink(""); }}>Create user</Button>
      </div>

      <Card style={{ padding: 0 }}>
        <div style={{ display: "flex", alignItems: "center", background: "var(--ui-bg)", borderBottom: "1px solid var(--ui-line-soft)", padding: "10px 26px" }}>
          <div className="uppercase-label" style={{ width: colW.name }}>User</div>
          <div className="uppercase-label" style={{ width: colW.role }}>Role</div>
          <div className="uppercase-label" style={{ width: colW.status }}>Status</div>
          <div className="uppercase-label" style={{ width: colW.last }}>Last login</div>
          <div className="uppercase-label" style={{ width: colW.action }} />
        </div>
        <div style={{ padding: "0 26px" }}>
          {users.map((u, i) => {
            const isSelf = u.email === user.email;
            return (
              <div key={u.email} className="flex items-center" style={{ padding: "15px 0", borderTop: i === 0 ? "none" : "1px solid var(--ui-line-soft)" }}>
                <div style={{ width: colW.name }}>
                  <div className="prose-i" style={{ fontSize: 14, fontWeight: 600, color: "var(--ui-text-hi)" }}>{u.name}</div>
                  <div className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)", marginTop: 2 }}>{u.email}{u.mustSetPassword ? " · setup pending" : ""}</div>
                </div>
                <div style={{ width: colW.role }}>
                  <select data-testid={`role-${u.email}`} value={u.role} disabled={isSelf} onChange={(e) => changeRole(u, e.target.value)} style={sel}>
                    <option value="super_admin">Super admin</option>
                    <option value="admin">Admin</option>
                    <option value="sales">Sales</option>
                  </select>
                </div>
                <div style={{ width: colW.status }}>
                  <SeverityPill severity={u.status === "disabled" ? "crit" : "good"} label={u.status === "disabled" ? "Disabled" : "Active"} />
                </div>
                <div style={{ width: colW.last }}>
                  <span className="num" style={{ fontSize: 12.5, color: "var(--ui-text-lo)" }}>{u.lastLogin ? fmtDateTime(u.lastLogin) : "—"}</span>
                </div>
                <div style={{ width: colW.action, display: "flex", gap: 8 }}>
                  <Button variant="secondary" onClick={() => reset(u)} style={{ padding: "6px 10px", fontSize: 12 }}>Reset</Button>
                  <Button variant="secondary" disabled={isSelf} onClick={() => toggleStatus(u)} style={{ padding: "6px 10px", fontSize: 12, opacity: isSelf ? 0.4 : 1 }}>{u.status === "disabled" ? "Enable" : "Disable"}</Button>
                  <Button variant="secondary" data-testid={`delete-${u.email}`} disabled={isSelf} onClick={() => del(u)} style={{ padding: "6px 10px", fontSize: 12, opacity: isSelf ? 0.4 : 1, color: isSelf ? undefined : "var(--sv-crit)" }}>Delete</Button>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ height: 12 }} />
      </Card>

      {createOpen && (
        <Modal title="Create user" onClose={() => setCreateOpen(false)}>
          {!setupLink ? (
            <>
              <label className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-mid)" }}>Name
                <input data-testid="new-user-name" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} /></label>
              <div style={{ height: 12 }} />
              <label className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-mid)" }}>Email
                <input data-testid="new-user-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} /></label>
              <div style={{ height: 12 }} />
              <label className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-mid)" }}>Role
                <select data-testid="new-user-role" value={role} onChange={(e) => setRole(e.target.value)} style={{ ...inputStyle }}>
                  <option value="sales">Sales</option><option value="admin">Admin</option><option value="super_admin">Super admin</option>
                </select></label>
              <div className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)", marginTop: 12 }}>No password is set here. A single-use setup link is generated for the user.</div>
              {err && <div className="prose-i" style={{ color: "var(--sv-crit)", fontSize: 12.5, marginTop: 10 }}>{err}</div>}
              <div className="flex" style={{ gap: 12, marginTop: 18, justifyContent: "flex-end" }}>
                <Button variant="secondary" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button data-testid="new-user-submit" onClick={create}>Create &amp; get link</Button>
              </div>
            </>
          ) : (
            <>
              <div className="prose-i" style={{ fontSize: 13.5, color: "var(--ui-text-hi)", marginBottom: 10 }}>User created. Share this single-use setup link — no password is ever shown:</div>
              <div className="num" data-testid="setup-link" style={{ fontSize: 12, color: "var(--ac-indigo-hi)", background: "var(--ui-bg)", border: "1px solid var(--ui-line)", borderRadius: 8, padding: "10px 12px", wordBreak: "break-all" }}>{setupLink}</div>
              <div className="flex" style={{ gap: 12, marginTop: 18, justifyContent: "flex-end" }}>
                <Button onClick={() => setCreateOpen(false)}>Done</Button>
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}
